import React, { useEffect, useState, useRef, useCallback } from "react";
import { connect } from "react-redux";
import { setBreadcrumbItems } from "../../store/actions";
import axios from "../../utils/axios";
import Swal from 'sweetalert2';
import { Link } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Row,
    Col,
    Card,
    CardBody,
    CardTitle,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    InputGroup,
    Badge,
    Input,
    Button,
    Progress,
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Table
} from 'reactstrap';
import moment from 'moment';
import { FiCircle, FiCheckCircle, FiEdit, FiTrash2, FiEye, FiMoreVertical, FiUpload } from 'react-icons/fi';
import { FaDotCircle } from "react-icons/fa";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { IoClose } from "react-icons/io5";

// Reusable PostModal component
const PostModal = ({
    isOpen,
    toggle,
    mode,
    post,
    categories,
    onSubmit,
    onFileSelect,
    onRemoveFile,
    previews,
    formData,
    onInputChange,
    onTextAreaChange,
    onTagsChange,
    onTagsKeyDown,
    minLength,
    maxLength,
    onPreview,
    showVisibilityModal,
    setShowVisibilityModal,
    passwordProtectionData,
    handlePasswordProtectionChange,
    passwordVisible,
    togglePasswordVisibility,
    handlePasswordChange,
    handleVisibilityModalSubmit,
    selectedStyle,
    resetStyles,
    handleStyleClick,
    styles
}) => {
    const fileInputRef = useRef(null);
    const darkThemeStyles = {
        card: {
            backgroundColor: '#222222',
            border: 'none',
            borderRadius: '8px'
        },
        text: {
            color: '#ffffff'
        },
        mutedText: {
            color: '#aaaaaa'
        },
        divider: {
            borderColor: '#333333'
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg" backdrop={false}>
            <ModalHeader toggle={toggle}>{mode === 'create' ? 'Create New Post' : 'Edit Post'}</ModalHeader>
            <Form onSubmit={onSubmit} onKeyDown={(e) => {
                if (e.key === 'Enter' && e.target.name !== 'tags') {
                    e.preventDefault();
                }
            }}>
                <ModalBody>
                    <FormGroup>
                        <Label>Title *</Label>
                        <Input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={onInputChange}
                            placeholder="Enter title"
                            required
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label>Category *</Label>
                        <Input
                            type="select"
                            name="category_id"
                            value={formData.category_id}
                            onChange={onInputChange}
                            required
                        >
                            <option value="">Select Category</option>
                            {categories.map(category => (
                                <option key={category.id} value={category.id}>
                                    {category.name}
                                </option>
                            ))}
                        </Input>
                    </FormGroup>

                    <FormGroup>
                        <Label>Tags</Label>
                        <Input
                            type="text"
                            name="tags"
                            onKeyDown={onTagsKeyDown}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (value.endsWith(',')) {
                                    const tag = value.slice(0, -1).trim();
                                    if (tag) {
                                        onInputChange({
                                            target: {
                                                name: 'tags',
                                                value: [...(formData.tags || []), tag]
                                            }
                                        });
                                        e.target.value = '';
                                    }
                                }
                            }}
                            placeholder="Enter tags separated by commas or press Enter"
                        />
                        <div className="d-flex flex-wrap gap-2 mt-2">
                            {formData.tags && formData.tags.map((tag, index) => (
                                <Badge
                                    key={index}
                                    color="primary"
                                    className="d-flex align-items-center"
                                    style={{ fontSize: '12px' }}
                                >
                                    {tag}
                                    <button
                                        type="button"
                                        className="btn-close btn-close-white ms-2"
                                        style={{ fontSize: '10px' }}
                                        onClick={() => {
                                            onInputChange({
                                                target: {
                                                    name: 'tags',
                                                    value: formData.tags.filter((_, i) => i !== index)
                                                }
                                            });
                                        }}
                                    />
                                </Badge>
                            ))}
                        </div>
                    </FormGroup>

                    <FormGroup>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <Label>Description (Minimum length 100 characters)*</Label>
                            <div className="d-flex justify-content-end gap-2 align-items-center">
                                <h4 className='custom-label'>Visibility:</h4>
                                <Badge
                                    color={
                                        formData.visibility === 'public' ? 'primary' :
                                            formData.visibility === 'private' ? 'danger' : 'warning'
                                    }
                                    className="custom-batch"
                                >
                                    {formData.visibility.charAt(0).toUpperCase() + formData.visibility.slice(1).replace('_', ' ')}
                                </Badge>
                                <Button
                                    color={'link'}
                                    onClick={() => setShowVisibilityModal(true)}
                                    className="p-1"
                                >
                                    <FiEdit size={18} />
                                </Button>
                            </div>
                        </div>
                        <div
                            className="position-relative"
                            style={{
                                minHeight: '200px',
                                marginBottom: '16px',
                                backgroundColor: formData.background_color || 'transparent',
                                borderRadius: '4px',
                                padding: '8px',
                                transition: 'background-color 0.3s ease',
                                border: '1px solid #dbdbdb'
                            }}
                        >
                            <textarea
                                value={formData.description}
                                name="description"
                                onChange={onTextAreaChange}
                                placeholder="Write something here..."
                                style={{
                                    width: '100%',
                                    minHeight: '170px',
                                    border: 'none',
                                    backgroundColor: 'transparent',
                                    resize: 'none',
                                    outline: 'none',
                                    color: formData.text_color || '#333',
                                    fontSize: '14px',
                                    lineHeight: '1.5'
                                }}
                            />
                            <div style={{ textAlign: 'right', fontSize: '12px', marginTop: '5px' }}>
                                <span style={{ color: formData.description.length < minLength ? 'red' : '#888' }}>
                                    {formData.description.length}/{maxLength}
                                </span>
                            </div>
                        </div>
                        <div className="d-flex justify-content-end align-items-center gap-2">
                            <Button
                                color="primary"
                                onClick={onPreview}
                                className="py-1 px-2"
                            >
                                <FiEye className="me-2" /> Preview
                            </Button>
                        </div>
                    </FormGroup>

                    <FormGroup>
                        <Label>Attachments</Label>
                        <div className="d-flex gap-2 flex-wrap">
                            <div className="border rounded p-2 text-center" style={{ width: '100px', height: '100px' }}>
                                <label htmlFor={`file-upload-${mode}`} className="d-flex flex-column align-items-center justify-content-center h-100 cursor-pointer">
                                    <FiUpload size={24} className="mb-1" />
                                    <small>Upload</small>
                                </label>
                                <input
                                    id={`file-upload-${mode}`}
                                    type="file"
                                    multiple
                                    ref={fileInputRef}
                                    onChange={onFileSelect}
                                    accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                                    style={{ display: 'none' }}
                                    disabled={selectedStyle}
                                />
                            </div>

                            {previews.images.map((preview, index) => (
                                <div key={`image-${index}`} className="position-relative">
                                    <img
                                        src={preview}
                                        alt=""
                                        className="border rounded"
                                        style={{ aspectRatio: '16/9', height: '100px', objectFit: 'cover' }}
                                    />
                                    <Button
                                        variant='danger'
                                        className="custom-close-btn"
                                        onClick={() => onRemoveFile('images', index)}
                                    >
                                        <IoClose size={18} />
                                    </Button>
                                </div>
                            ))}

                            {previews.videos.map((preview, index) => (
                                <div key={`video-${index}`} className="position-relative">
                                    <video
                                        src={preview}
                                        className="border rounded"
                                        style={{ width: '100px', height: '100px' }}
                                        controls
                                    />
                                    <Button
                                        close
                                        className="position-absolute top-0 end-0 translate-middle custom-close-btn"
                                        onClick={() => onRemoveFile('videos', index)}
                                    />
                                </div>
                            ))}

                            {previews.documents.map((preview, index) => (
                                <div key={`doc-${index}`} className="position-relative border rounded p-2" style={{ width: '100px', height: '100px' }}>
                                    <div className="d-flex flex-column align-items-center justify-content-center h-100">
                                        <i className="ri-file-text-line fs-4 mb-1"></i>
                                        <small className="text-truncate w-100 text-center">
                                            {preview}
                                        </small>
                                    </div>
                                    <Button
                                        close
                                        className="position-absolute top-0 end-0 translate-middle"
                                        onClick={() => onRemoveFile('documents', index)}
                                    />
                                </div>
                            ))}
                        </div>
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggle}>
                        Cancel
                    </Button>
                    <Button color="primary" type="submit">
                        {mode === 'create' ? 'Create Post' : 'Update Post'}
                    </Button>
                </ModalFooter>
            </Form>
        </Modal>
    );
};

const FeedsManagement = (props) => {
    document.title = "Feeds Management | Equity Circle - Admin Dashboard";
    const breadcrumbItems = [
        { title: "Feeds Management", link: "#" },
        { title: "All Feeds", link: "#" },
    ];

    useEffect(() => {
        props.setBreadcrumbItems('Feeds Management', breadcrumbItems);
    }, []);

    // State for posts
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('all');

    // Modal states
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
    const [viewModal, setViewModal] = useState(false);
    const [currentPost, setCurrentPost] = useState(null);

    // Form data
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        category_id: '',
        images: [],
        videos: [],
        documents: [],
        visibility: 'public',
        password: '',
        tags: []
    });

    const handleTagsChange = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const value = e.target.value.trim();
            if (value) {
                setFormData(prev => ({
                    ...prev,
                    tags: [...(prev.tags || []), value]
                }));
                e.target.value = '';
            }
        } else {
            const value = e.target.value;
            // Check if the last character is a comma
            if (value.endsWith(',')) {
                const tag = value.slice(0, -1).trim();
                if (tag) {
                    setFormData(prev => ({
                        ...prev,
                        tags: [...(prev.tags || []), tag]
                    }));
                    e.target.value = '';
                }
            }
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && e.target.name !== 'tags') {
            e.preventDefault();
        }
    };

    const handleTagsKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const value = e.target.value.trim();
            if (value) {
                setFormData(prev => ({
                    ...prev,
                    tags: [...(prev.tags || []), value]
                }));
                e.target.value = '';
            }
        }
    };

    const minLength = 100;
    const maxLength = 2000;

    const handleTextAreaChange = (e) => {
        const { name, value } = e.target;
        if (value.length <= maxLength) {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const [selectedFiles, setSelectedFiles] = useState({ images: [], videos: [], documents: [] });
    const [previews, setPreviews] = useState({ images: [], videos: [], documents: [] });
    const fileInputRef = useRef(null);

    // Visibility modal
    const [showVisibilityModal, setShowVisibilityModal] = useState(false);
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [passwordProtectionData, setPasswordProtectionData] = useState({
        type: 'public',
        password: ''
    });

    // Background styles
    const [selectedStyle, setSelectedStyle] = useState(null);
    const styles = [
        { name: "Black", color: "#000000", text: '#fff' },
        { name: "Orange", color: "#FFAA33", text: '#000' },
        { name: "Brown", color: "#B45309", text: '#fff' },
        { name: "Blue", color: "#2563EB", text: '#fff' },
        { name: "Purple", color: "#9333EA", text: '#fff' },
        { name: "Pink", color: "#F500F5", text: '#fff' },
    ];

    // State for search and pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [perPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchTimeout, setSearchTimeout] = useState(null);

    // Fetch posts
    const fetchPosts = async (pageNumber = 1, category = 'all', search = '') => {
        setLoading(true);
        try {
            let url = `/api/posts-crm?page=${pageNumber}&per_page=${perPage}`;
            if (category !== 'all') {
                url += `&category=${category}`;
            }
            if (search) {
                url += `&search=${encodeURIComponent(search)}`;
            }

            const response = await axios.get(url);
            setPosts(response.data.data);
            setCurrentPage(parseInt(response.data.current_page, 10));
            setTotalPages(parseInt(response.data.last_page, 10));
            setHasMore(response.data.next_page_url !== null);
        } catch (error) {
            console.error('Error fetching posts:', error);
            toast.error('Failed to fetch posts');
        } finally {
            setLoading(false);
        }
    };

    // Fetch categories
    const fetchCategories = async () => {
        try {
            const response = await axios.get('/api/categories');
            setCategories(response.data);
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    useEffect(() => {
        fetchPosts();
        fetchCategories();
    }, []);

    // Handle file selection
    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        const newImages = [];
        const newVideos = [];
        const newDocuments = [];
        const imagePreviewUrls = [];
        const videoPreviewUrls = [];
        const documentPreviewUrls = [];

        files.forEach(file => {
            if (file.type.startsWith('image/')) {
                newImages.push(file);
                imagePreviewUrls.push(URL.createObjectURL(file));
            } else if (file.type.startsWith('video/')) {
                newVideos.push(file);
                videoPreviewUrls.push(URL.createObjectURL(file));
            } else if (
                file.type === 'application/pdf' ||
                file.type === 'application/msword' ||
                file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                file.type === 'application/vnd.ms-excel' ||
                file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                file.type === 'application/vnd.ms-powerpoint' ||
                file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
                file.type === 'text/plain'
            ) {
                newDocuments.push(file);
                documentPreviewUrls.push(file.name);
            }
        });

        setSelectedFiles(prev => ({
            images: [...prev.images, ...newImages],
            videos: [...prev.videos, ...newVideos],
            documents: [...prev.documents, ...newDocuments]
        }));

        setPreviews(prev => ({
            images: [...prev.images, ...imagePreviewUrls],
            videos: [...prev.videos, ...videoPreviewUrls],
            documents: [...prev.documents, ...documentPreviewUrls]
        }));

        setFormData(prev => ({
            ...prev,
            images: [...prev.images, ...newImages],
            videos: [...prev.videos, ...newVideos],
            documents: [...prev.documents, ...newDocuments]
        }));
    };

    // Remove file
    const removeFile = (type, index) => {
        const newFiles = { ...selectedFiles };
        const newPreviews = { ...previews };
        const newFormData = { ...formData };

        if (type === 'images' || type === 'videos') {
            URL.revokeObjectURL(newPreviews[type][index]);
        }

        newFiles[type].splice(index, 1);
        newPreviews[type].splice(index, 1);
        newFormData[type].splice(index, 1);

        setSelectedFiles(newFiles);
        setPreviews(newPreviews);
        setFormData(newFormData);
    };

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle visibility change
    const handleVisibilityChange = (visibility) => {
        setFormData(prev => ({
            ...prev,
            visibility
        }));
    };

    // Handle password protection change
    const handlePasswordProtectionChange = (type) => {
        setPasswordProtectionData(prev => ({
            ...prev,
            type
        }));
    };

    // Toggle password visibility
    const togglePasswordVisibility = () => {
        setPasswordVisible(!passwordVisible);
    };

    // Handle password change
    const handlePasswordChange = (e) => {
        setPasswordProtectionData(prev => ({
            ...prev,
            password: e.target.value
        }));
    };

    // Apply visibility settings
    const handleVisibilityModalSubmit = () => {
        setFormData(prev => ({
            ...prev,
            visibility: passwordProtectionData.type,
            password: passwordProtectionData.type === 'password_protected'
                ? passwordProtectionData.password
                : ''
        }));
        setShowVisibilityModal(false);
    };

    // Function to determine text color based on background color (YIQ formula)
    const getContrastYIQ = (hexcolor) => {
        // Remove # if it's there
        hexcolor = hexcolor.replace('#', '');

        // Convert to RGB
        const r = parseInt(hexcolor.substr(0, 2), 16);
        const g = parseInt(hexcolor.substr(2, 2), 16);
        const b = parseInt(hexcolor.substr(4, 2), 16);

        // Calculate YIQ (brightness) value
        const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;

        // Return true for dark text, false for light text
        return yiq >= 155;
    };

    // Handle style selection for background
    const handleStyleClick = (style) => {
        const isDarkBackground = !getContrastYIQ(style.color);
        const newStyle = selectedStyle === style ? null : style;

        setSelectedStyle(newStyle);

        if (newStyle) {
            setFormData(prev => ({
                ...prev,
                background_color: style.color,
                text_color: isDarkBackground ? '#ffffff' : '#000000'
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                background_color: '',
                text_color: '#000000'
            }));
        }
    };

    // Reset styles
    const resetStyles = () => {
        setSelectedStyle(null);
        setFormData(prev => ({
            ...prev,
            background_color: '',
            text_color: '#000000'
        }));
    };

    // Open create modal
    const openCreateModal = () => {
        setFormData({
            title: '',
            description: '',
            category_id: '',
            images: [],
            videos: [],
            documents: [],
            visibility: 'public',
            password: '',
            tags: []
        });
        setSelectedFiles({ images: [], videos: [], documents: [] });
        setPreviews({ images: [], videos: [], documents: [] });
        setSelectedStyle(null);
        setModalMode('create');
        setModalOpen(true);
    };

    // Open edit modal
    const openEditModal = (post) => {
        setCurrentPost(post);

        // Prepare form data from post
        const description = post.description || '';
        let extractedText = '';
        let backgroundColor = '';

        if (description.includes('<div') && description.includes('</div>')) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(description, 'text/html');
            const div = doc.querySelector('div');

            if (div) {
                extractedText = div.textContent || div.innerText;
                const style = div.getAttribute('style');
                if (style) {
                    const styleObj = {};
                    style.split(';').forEach(part => {
                        const [key, value] = part.split(':').map(str => str.trim());
                        if (key && value) styleObj[key] = value;
                    });
                    backgroundColor = styleObj['background'] || styleObj['background-color'];
                }
            }
        }

        setFormData({
            title: post.title || '',
            description: extractedText || post.description || '',
            category_id: post.category_id || '',
            images: JSON.parse(post.images) || [],
            videos: JSON.parse(post.videos) || [],
            documents: JSON.parse(post.documents) || [],
            visibility: post.visibility || 'public',
            password: post.password || '',
            tags: post.tags && Array.isArray(post.tags) ? post.tags.map(tag => tag.name) : []
        });

        // Prepare previews
        const baseurl = process.env.REACT_APP_BACKEND_BASE_URL;
        const documents = JSON.parse(post.documents);
        const images = JSON.parse(post.images);
        const videos = JSON.parse(post.videos);
        setPreviews({
            images: images?.map(img => `${baseurl}/data/images/${img}`) || [],
            videos: videos?.map(video => `${baseurl}/data/videos/${video}`) || [],
            documents: documents?.map(doc => doc.split('/').pop()) || []
        });

        setSelectedFiles({
            images: images || [],
            videos: videos || [],
            documents: documents || []
        });

        // Set background style if exists
        if (backgroundColor) {
            const matchedStyle = styles.find(style =>
                style.color.toLowerCase() === backgroundColor.toLowerCase()
            );
            if (matchedStyle) {
                setSelectedStyle(matchedStyle);
            }
        }

        setModalMode('edit');
        setModalOpen(true);
    };

    // Open view modal
    const openViewModal = (post) => {
        setCurrentPost(post);
        setViewModal(true);
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.title || !formData.description || !formData.category_id) {
            toast.error('Please fill all required fields');
            return;
        }

        const formDataToSubmit = new FormData();
        if (formData.tags && formData.tags.length > 0) {
            formData.tags.forEach(tag => {
                formDataToSubmit.append('tags[]', tag);
            });
        }

        formDataToSubmit.append('visibility', formData.visibility || 'public');
        formDataToSubmit.append('title', formData.title);

        if (selectedStyle) {
            const div = document.createElement('div');
            div.style.background = selectedStyle.color;
            div.style.color = selectedStyle.textColor;
            div.style.fontSize = "18px";
            div.style.textAlign = "center";
            div.style.minHeight = "250px";
            div.style.padding = "30px 20px";
            div.innerHTML = formData.description;
            formDataToSubmit.append('description', new XMLSerializer().serializeToString(div));
        } else {
            formDataToSubmit.append('description', formData.description);
        }

        formDataToSubmit.append('category_id', formData.category_id);

        if (modalMode === 'edit' && currentPost) {
            formDataToSubmit.append('_method', 'PUT');
        }

        const keptImages = formData.images.filter(img => typeof img === 'string');
        const keptVideos = formData.videos.filter(video => typeof video === 'string');
        const keptDocuments = formData.documents.filter(doc => typeof doc === 'string');

        if (keptImages.length > 0) {
            formDataToSubmit.append('kept_images', JSON.stringify(keptImages));
        }
        if (keptVideos.length > 0) {
            formDataToSubmit.append('kept_videos', JSON.stringify(keptVideos));
        }
        if (keptDocuments.length > 0) {
            formDataToSubmit.append('kept_documents', JSON.stringify(keptDocuments));
        }

        const newImages = formData.images.filter(img => img instanceof File);
        const newVideos = formData.videos.filter(video => video instanceof File);
        const newDocuments = formData.documents.filter(doc => doc instanceof File);

        newImages.forEach((file, index) => {
            formDataToSubmit.append(`images[${index}]`, file);
        });
        newVideos.forEach((file, index) => {
            formDataToSubmit.append(`videos[${index}]`, file);
        });
        newDocuments.forEach((file, index) => {
            formDataToSubmit.append(`documents[${index}]`, file);
        });

        if (formData.visibility === 'password_protected') {
            formDataToSubmit.append('password', formData.password);
        }

        try {
            let response;
            if (modalMode === 'edit' && currentPost) {
                response = await axios.post(`/api/posts/${currentPost.id}`, formDataToSubmit, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    }
                });
                toast.success('Post updated successfully!');
            } else {
                response = await axios.post('/api/posts', formDataToSubmit, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    }
                });
                toast.success('Post created successfully!');
            }

            fetchPosts();
            setModalOpen(false);
        } catch (error) {
            console.error('Error saving post:', error);
            toast.error(error.response?.data?.message || 'Something went wrong while saving the post!');
        }
    };

    const [previewModal, setPreviewModal] = useState(false);

    const handlePreview = () => {
        if (!formData.description) {
            toast.error('Please add title and description to preview');
            return;
        }

        if (formData.description.length < 100) {
            toast.error('Please add more description to preview');
            return;
        }
        setPreviewModal(true);
    };

    // Delete post
    const deletePost = async (postId) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await axios.delete(`/api/posts/${postId}`);
                toast.success('Post deleted successfully!');
                fetchPosts();
            } catch (error) {
                console.error('Error deleting post:', error);
                toast.error('Failed to delete post');
            }
        }
    };

    // Toggle post status
    const togglePostStatus = async (postId, currentStatus) => {
        try {
            await axios.put(`/api/posts/${postId}/status`, {
                status: currentStatus === 'active' ? 'inactive' : 'active'
            });
            toast.success(`Post ${currentStatus === 'active' ? 'deactivated' : 'activated'} successfully!`);
            fetchPosts();
        } catch (error) {
            console.error('Error toggling post status:', error);
            toast.error('Failed to toggle post status');
        }
    };

    // Handle search input
    const handleSearch = (e) => {
        const value = e.target.value;
        setSearchTerm(value);

        // Clear previous timeout
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        // Set a new timeout to avoid too many API calls
        setSearchTimeout(
            setTimeout(() => {
                fetchPosts(1, selectedCategory, value);
            }, 500)
        );
    };

    // Filter posts by category
    const filterByCategory = (category) => {
        setSelectedCategory(category);
        fetchPosts(1, category);
    };

    // Get file extension
    const getFileExtension = (file) => {
        if (file instanceof File) {
            return file.name.split('.').pop();
        }
        if (typeof file === 'string') {
            return file.split('.').pop();
        }
        return '';
    };

    const darkThemeStyles = {
        card: {
            backgroundColor: '#222222',
            border: 'none',
            borderRadius: '8px'
        },
        text: {
            color: '#ffffff'
        },
        mutedText: {
            color: '#aaaaaa'
        },
        divider: {
            borderColor: '#333333'
        }
    };

    return (
        <React.Fragment>
            <ToastContainer position="top-right" autoClose={5000} />

            <Row>
                <Col xs="12">
                    <Card>
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <CardTitle tag="h4" className="mb-0">Posts Management</CardTitle>
                                <div className="d-flex justify-content-end gap-2 align-items-center">
                                    <div className="position-relative" style={{ maxWidth: '500px', minWidth: '350px', width: '100%' }}>
                                        <Input
                                            type="text"
                                            className="form-control bg-light rounded"
                                            placeholder="Search by title, category, or status..."
                                            value={searchTerm}
                                            onChange={handleSearch}
                                        />
                                        <i className="ri-search-line search-icon"></i>
                                    </div>
                                    <Button color="primary" style={{ minWidth: 'fit-content' }} onClick={openCreateModal}>
                                        Create New Post
                                    </Button>
                                </div>
                            </div>

                            {/* Search and Filter Section */}
                            <div className="mb-4">
                                <div className="d-flex flex-wrap gap-2 justify-content-start">
                                    <Button
                                        color={selectedCategory === 'all' ? 'primary' : 'light'}
                                        onClick={() => filterByCategory('all')}
                                        className="mb-2 mb-md-0"
                                    >
                                        All
                                    </Button>
                                    {categories.map(category => (
                                        <Button
                                            key={category.id}
                                            color={selectedCategory === category.id.toString() ? 'primary' : 'light'}
                                            onClick={() => filterByCategory(category.id.toString())}
                                            className="mb-2 mb-md-0"
                                        >
                                            {category.name}
                                        </Button>
                                    ))}
                                </div>
                            </div>

                            {/* Posts table */}
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="sr-only">Loading...</span>
                                    </div>
                                </div>
                            ) : posts.length === 0 ? (
                                <div className="text-center py-5">
                                    <h5>No posts found</h5>
                                    <p>Create your first post by clicking the button above</p>
                                </div>
                            ) : (
                                <div className="mb-4">
                                    <Table hover className="mb-0">
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>
                                                    <div className="d-flex align-items-center">
                                                        Title
                                                        <i className="ri-arrow-up-down-line ms-1" style={{ fontSize: '16px' }}></i>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="d-flex align-items-center">
                                                        Created By
                                                        <i className="ri-arrow-up-down-line ms-1" style={{ fontSize: '16px' }}></i>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="d-flex align-items-center">
                                                        Category
                                                        <i className="ri-arrow-up-down-line ms-1" style={{ fontSize: '16px' }}></i>
                                                    </div>
                                                </th>
                                                <th>Visibility</th>
                                                <th>
                                                    <div className="d-flex align-items-center">
                                                        Status
                                                        <i className="ri-arrow-up-down-line ms-1" style={{ fontSize: '16px' }}></i>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="d-flex align-items-center">
                                                        Created At
                                                        <i className="ri-arrow-up-down-line ms-1" style={{ fontSize: '16px' }}></i>
                                                    </div>
                                                </th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {posts.map((post, index) => (
                                                <tr key={post.id}>
                                                    <th scope="row">{index + 1}</th>
                                                    <td>
                                                        <Link to="#" onClick={() => openViewModal(post)}>
                                                            {post.title}
                                                        </Link>
                                                    </td>
                                                    <td>
                                                        <Link to="#" onClick={() => openViewModal(post)}>
                                                            {post?.user?.name}
                                                        </Link>
                                                    </td>
                                                    <td>
                                                        {categories.find(c => c.id === post.category_id)?.name || 'N/A'}
                                                    </td>
                                                    <td>
                                                        <Badge
                                                            color={
                                                                post.visibility === 'public' ? 'primary' :
                                                                    post.visibility === 'private' ? 'danger' : 'warning'
                                                            }
                                                        >
                                                            {post.visibility.charAt(0).toUpperCase() + post.visibility.slice(1).replace('_', ' ')}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <Badge color={post.status === 'active' ? 'success' : 'secondary'}>
                                                            {post.status}
                                                        </Badge>
                                                    </td>
                                                    <td>{moment(post.created_at).format('MMM D, YYYY h:mm A')}</td>
                                                    <td>
                                                        <Dropdown isOpen={post.dropdownOpen} toggle={() => {
                                                            setPosts(posts.map(p =>
                                                                p.id === post.id
                                                                    ? { ...p, dropdownOpen: !p.dropdownOpen }
                                                                    : { ...p, dropdownOpen: false }
                                                            ));
                                                        }}>
                                                            <DropdownToggle tag="a" className="text-muted">
                                                                <FiMoreVertical />
                                                            </DropdownToggle>
                                                            <DropdownMenu className="custom-dropdown-menu">
                                                                <DropdownItem onClick={() => openViewModal(post)}>
                                                                    <FiEye className="me-2" /> View
                                                                </DropdownItem>
                                                                <DropdownItem onClick={() => openEditModal(post)}>
                                                                    <FiEdit className="me-2" /> Edit
                                                                </DropdownItem>
                                                                <DropdownItem onClick={() => togglePostStatus(post.id, post.status)}>
                                                                    {post.status === 'active' ? (
                                                                        <>
                                                                            <FiCircle className="me-2" /> Deactivate
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <FiCheckCircle className="me-2" /> Activate
                                                                        </>
                                                                    )}
                                                                </DropdownItem>
                                                                <DropdownItem onClick={() => deletePost(post.id)} className="text-danger">
                                                                    <FiTrash2 className="me-2" /> Delete
                                                                </DropdownItem>
                                                            </DropdownMenu>
                                                        </Dropdown>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>

                                    {/* Pagination */}
                                    {totalPages > 1 && (
                                        <div className="d-flex justify-content-between align-items-center mt-3">
                                            <div>
                                                <p className="mb-0 text-muted">
                                                    Showing {posts.length} of {totalPages * perPage} entries
                                                </p>
                                            </div>
                                            <nav>
                                                <ul className="pagination mb-0">
                                                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                        <button
                                                            className="page-link"
                                                            onClick={() => fetchPosts(currentPage - 1, selectedCategory)}
                                                            disabled={currentPage === 1}
                                                        >
                                                            <i className="mdi mdi-chevron-left"></i>
                                                        </button>
                                                    </li>

                                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                        let pageNum;
                                                        if (totalPages <= 5) {
                                                            pageNum = i + 1;
                                                        } else if (currentPage <= 3) {
                                                            pageNum = i + 1;
                                                        } else if (currentPage >= totalPages - 2) {
                                                            pageNum = totalPages - 4 + i;
                                                        } else {
                                                            pageNum = currentPage - 2 + i;
                                                        }

                                                        return (
                                                            <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                                                                <button
                                                                    className="page-link"
                                                                    onClick={() => fetchPosts(pageNum, selectedCategory)}
                                                                >
                                                                    {pageNum}
                                                                </button>
                                                            </li>
                                                        );
                                                    })}

                                                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                        <button
                                                            className="page-link"
                                                            onClick={() => fetchPosts(currentPage + 1, selectedCategory)}
                                                            disabled={currentPage === totalPages}
                                                        >
                                                            <i className="mdi mdi-chevron-right"></i>
                                                        </button>
                                                    </li>
                                                </ul>
                                            </nav>
                                        </div>
                                    )}
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Combined Create/Edit Post Modal */}
            <PostModal
                isOpen={modalOpen}
                toggle={() => setModalOpen(false)}
                mode={modalMode}
                post={currentPost}
                categories={categories}
                onSubmit={handleSubmit}
                onFileSelect={handleFileSelect}
                onRemoveFile={removeFile}
                previews={previews}
                formData={formData}
                onInputChange={handleInputChange}
                onTextAreaChange={handleTextAreaChange}
                onTagsChange={handleTagsChange}
                onTagsKeyDown={handleTagsKeyDown}
                minLength={minLength}
                maxLength={maxLength}
                onPreview={handlePreview}
                showVisibilityModal={showVisibilityModal}
                setShowVisibilityModal={setShowVisibilityModal}
                passwordProtectionData={passwordProtectionData}
                handlePasswordProtectionChange={handlePasswordProtectionChange}
                passwordVisible={passwordVisible}
                togglePasswordVisibility={togglePasswordVisibility}
                handlePasswordChange={handlePasswordChange}
                handleVisibilityModalSubmit={handleVisibilityModalSubmit}
                selectedStyle={selectedStyle}
                resetStyles={resetStyles}
                handleStyleClick={handleStyleClick}
                styles={styles}
            />

            <Modal
                isOpen={previewModal}
                toggle={() => setPreviewModal(false)}
                size="md"
                centered
                contentClassName="border-0"
                style={darkThemeStyles.modal}
                backdrop={false}
            >
                <ModalBody className="p-0 position-relative">
                    {/* Custom close button */}
                    <button
                        onClick={() => setPreviewModal(false)}
                        style={{
                            position: 'absolute',
                            top: '10px',
                            right: '10px',
                            width: '30px',
                            height: '30px',
                            borderRadius: '50%',
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            border: 'none',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 1000,
                            cursor: 'pointer'
                        }}
                    >
                        <IoClose size={18} />
                    </button>

                    <Card className="m-0" style={darkThemeStyles.card}>
                        <CardBody>
                            {/* Header with avatar and username */}
                            <div className="d-flex align-items-start mb-0">
                                <div
                                    style={{
                                        minWidth: '40px',
                                        height: '40px',
                                        borderRadius: '50%',
                                        backgroundColor: '#333333',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginRight: '10px'
                                    }}
                                >
                                    <span style={{ fontWeight: 'bold', color: '#ffffff' }}>GS</span>
                                </div>
                                <div>
                                    <div className="d-flex justify-content-start gap-1 align-items-center mb-4 mt-2">
                                        <h6 style={{ margin: 0, fontWeight: 'bold', color: '#ffffff' }}>EQUITY CIRCLE</h6>
                                        <small style={darkThemeStyles.mutedText}>@equitycircle</small>
                                    </div>

                                    {/* Post content */}
                                    <div className="custom-font-family" style={{
                                        marginBottom: '15px',
                                        backgroundColor: formData.background_color || 'transparent',
                                        color: formData.text_color || '#ffffff',
                                        padding: formData.background_color ? '15px' : '0',
                                        borderRadius: formData.background_color ? '8px' : '0'
                                    }}>
                                        <h5 style={{ fontWeight: '400', marginBottom: '6px', color: formData.text_color || '#aaaaaa' }}>
                                            {formData.title || "Let's talk progressive overload"}
                                        </h5>
                                        {formData.description ? (
                                            <p style={{ whiteSpace: 'pre-wrap', marginBottom: '0', lineHeight: '1.5', wordBreak: 'break-all', color: formData.text_color || '#aaaaaa' }}>
                                                {formData.description}
                                            </p>
                                        ) : (
                                            <p style={{ whiteSpace: 'pre-wrap', marginBottom: '0', lineHeight: '1.5', wordBreak: 'break-all', color: formData.text_color || '#aaaaaa' }}>
                                                If you're not increasing reps, sets, or intensity weekly, you're not growing. Here's a quick strategy:
                                                <br />
                                                <br />
                                                1. Add 1-2 reps weekly
                                                <br />
                                                2. Increase load bi-weekly
                                                <br />
                                                3. Track every set
                                            </p>
                                        )}

                                    </div>

                                    {/* Hashtags */}
                                    <div style={{ marginTop: '10px' }}>
                                        {formData.tags && formData.tags.length > 0 ? (
                                            formData.tags.map((tag, index) => (
                                                <small key={index} style={{ color: '#4dabf7', marginRight: '4px' }}>
                                                    #{tag}
                                                </small>
                                            ))
                                        ) : (
                                            <small style={{ color: '#4dabf7' }}>#MuscleGrowth #Hypertrophy #TrainingTips</small>
                                        )}
                                    </div>

                                    {/* Attachments preview - only showing first image like in the example */}
                                    {previews.images.length > 0 && (
                                        <div className="mb-2">
                                            <img
                                                src={previews.images[0]}
                                                alt="Preview"
                                                style={{
                                                    width: '100%',
                                                    borderRadius: '8px',
                                                    marginBottom: '5px',
                                                    maxHeight: '300px',
                                                    objectFit: 'cover',
                                                    aspectRatio: '16/8'
                                                }}
                                            />
                                        </div>
                                    )}

                                    {/* Likes/comments section - exactly like the image */}
                                    <div className="d-flex justify-content-start gap-3 mt-2 pt-2" style={darkThemeStyles.divider}>
                                        <small style={darkThemeStyles.mutedText} className="d-flex align-items-center gap-1">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="11" viewBox="0 0 13 11" fill="none">
                                                <path d="M9.19225 0.228027C8.62945 0.236781 8.07893 0.393887 7.59628 0.683479C7.11362 0.973071 6.71593 1.3849 6.44336 1.87736C6.17079 1.3849 5.7731 0.973071 5.29044 0.683479C4.80779 0.393887 4.25727 0.236781 3.69447 0.228027C2.7973 0.267007 1.95202 0.65948 1.34331 1.3197C0.734592 1.97993 0.411917 2.85423 0.445779 3.75161C0.445779 6.02419 2.83781 8.50619 4.844 10.189C5.29193 10.5654 5.85827 10.7718 6.44336 10.7718C7.02845 10.7718 7.59479 10.5654 8.04271 10.189C10.0489 8.50619 12.4409 6.02419 12.4409 3.75161C12.4748 2.85423 12.1521 1.97993 11.5434 1.3197C10.9347 0.65948 10.0894 0.267007 9.19225 0.228027Z" fill="#E05F5F" />
                                            </svg>
                                            12
                                        </small>
                                        <small style={darkThemeStyles.mutedText} className="d-flex align-items-center gap-1">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
                                                <path d="M6.44761 12.4165C6.2096 12.4165 5.97009 12.3325 5.77908 12.163L3.905 10.5844H2.44344C1.3404 10.5844 0.44336 9.68738 0.44336 8.58433V2.58358C0.44286 1.48053 1.3399 0.583496 2.44294 0.583496H10.4433C11.5463 0.583496 12.4434 1.48053 12.4434 2.58358V8.58383C12.4434 9.68688 11.5463 10.5839 10.4433 10.5839H9.01722L7.09364 12.174C6.91263 12.335 6.68112 12.416 6.44711 12.416L6.44761 12.4165ZM2.44294 1.58354C1.89142 1.58354 1.4429 2.03206 1.4429 2.58358V8.58383C1.4429 9.13535 1.89142 9.58387 2.44294 9.58387H4.08751C4.20552 9.58387 4.31952 9.62537 4.41002 9.70138L6.43361 11.4064L8.51869 9.69838C8.6087 9.62437 8.7212 9.58387 8.83771 9.58387H10.4438C10.9953 9.58387 11.4438 9.13535 11.4438 8.58383V2.58358C11.4438 2.03206 10.9953 1.58354 10.4438 1.58354H2.44294Z" fill="#848484" />
                                            </svg>
                                            81
                                        </small>
                                        <small style={darkThemeStyles.mutedText} className="d-flex align-items-center gap-1">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
                                                <path d="M10.112 7.83181C9.73442 7.83212 9.36256 7.9243 9.02853 8.10041C8.6945 8.27652 8.40834 8.53126 8.19473 8.84265L4.94397 7.37483C5.17486 6.81729 5.17575 6.19104 4.94646 5.63283L8.19274 4.15753C8.50931 4.61538 8.9795 4.9446 9.51801 5.08548C10.0565 5.22636 10.6277 5.16957 11.1279 4.92539C11.6282 4.68122 12.0243 4.26585 12.2445 3.75462C12.4647 3.24339 12.4944 2.67017 12.3282 2.13893C12.1619 1.60769 11.8108 1.15362 11.3385 0.859089C10.8662 0.564556 10.3039 0.449068 9.75372 0.533573C9.20353 0.618077 8.70185 0.896978 8.33969 1.31968C7.97752 1.74239 7.77885 2.2809 7.77971 2.83754C7.78183 2.96929 7.79519 3.10062 7.81966 3.23009L4.36863 4.79829C4.03718 4.48776 3.62224 4.2808 3.17478 4.20285C2.72733 4.12491 2.26685 4.17936 1.84992 4.35952C1.43299 4.53969 1.07777 4.83771 0.827888 5.21699C0.57801 5.59627 0.444363 6.04028 0.443365 6.49447C0.442367 6.94866 0.574062 6.39325 0.822271 6.77362C1.07048 7.15399 1.42439 7.45358 1.84053 7.63557C2.25666 7.81757 2.7169 7.87404 3.16469 7.79806C3.61248 7.72208 4.02832 7.51695 4.36114 7.20788L7.82116 7.77009C7.79713 7.89944 7.78393 8.0306 7.78171 8.1621C7.78161 8.6232 7.91824 9.0739 8.17432 9.4573C8.43039 9.8406 8.79441 10.1395 9.22034 10.3159C9.64626 10.4924 10.115 10.5386 10.5671 10.4487C11.0193 10.3588 11.4347 10.1368 11.7607 9.8108C12.0867 9.4848 12.3087 9.0694 12.3986 8.6172C12.4885 8.1651 12.4423 7.69636 12.2658 7.27044C12.0894 6.84451 11.7905 6.48049 11.4072 6.22442C11.0238 5.96834 10.5731 5.83171 10.112 5.83181Z" fill="#848484" />
                                            </svg>
                                            12
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </ModalBody>
            </Modal>

            <Modal
                isOpen={viewModal}
                toggle={() => setViewModal(false)}
                size="md"
                centered
                contentClassName="border-0"
                style={darkThemeStyles.modal}
                backdrop={false}
            >
                <ModalBody className="p-0 position-relative">
                    {/* Custom close button */}
                    <button
                        onClick={() => setViewModal(false)}
                        style={{
                            position: 'absolute',
                            top: '10px',
                            right: '10px',
                            width: '30px',
                            height: '30px',
                            borderRadius: '50%',
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            border: 'none',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 1000,
                            cursor: 'pointer'
                        }}
                    >
                        <IoClose size={18} />
                    </button>

                    <Card className="m-0" style={darkThemeStyles.card}>
                        <CardBody>
                            {/* Header with avatar and username */}
                            <div className="d-flex align-items-start mb-0">
                                <div
                                    style={{
                                        minWidth: '40px',
                                        height: '40px',
                                        borderRadius: '50%',
                                        backgroundColor: '#333333',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginRight: '10px'
                                    }}
                                >
                                    <span style={{ fontWeight: 'bold', color: '#ffffff' }}>GS</span>
                                </div>
                                <div>
                                    <div className="d-flex justify-content-start gap-1 align-items-center mb-4 mt-2">
                                        <h6 style={{ margin: 0, fontWeight: 'bold', color: '#ffffff' }}>EQUITY CIRCLE</h6>
                                        <small style={darkThemeStyles.mutedText}>@equitycircle</small>
                                    </div>

                                    {/* Post content */}
                                    <div className="custom-font-family" style={{
                                        marginBottom: '15px',
                                        backgroundColor: 'transparent',
                                        color: '#ffffff',
                                    }}>
                                        <h5 style={{ fontWeight: '400', marginBottom: '6px', color: formData.text_color || '#aaaaaa' }}>
                                            {currentPost?.title || ''}
                                        </h5>
                                        <p style={{ whiteSpace: 'pre-wrap', marginBottom: '0', lineHeight: '1.5', wordBreak: 'break-all', color: formData.text_color || '#aaaaaa' }}>
                                            {currentPost?.description || ''}
                                        </p>

                                    </div>

                                    {/* Hashtags */}
                                    <div style={{ marginTop: '10px' }}>
                                        {currentPost?.tags.map((tag, index) => (
                                            <small key={index} style={{ color: '#4dabf7', marginRight: '4px' }}>
                                                #{tag.name}
                                            </small>
                                        ))}
                                    </div>

                                    {/* Attachments preview - only showing first image like in the example */}

                                    {currentPost?.images && JSON.parse(currentPost.images).length > 0 && (

                                        JSON.parse(currentPost.images).map((image, index) => (
                                            index == 0 && (
                                                <div className="mb-2">
                                                    <img
                                                        key={`view-image-${index}`}
                                                        src={`${process.env.REACT_APP_BACKEND_BASE_URL}/data/images/${image}`}
                                                        alt="Preview"
                                                        style={{
                                                            width: '100%',
                                                            borderRadius: '8px',
                                                            marginBottom: '5px',
                                                            maxHeight: '300px',
                                                            objectFit: 'cover',
                                                            aspectRatio: '16/8'
                                                        }}
                                                    />
                                                </div>
                                            )
                                        ))
                                    )}

                                    {/* Likes/comments section - exactly like the image */}
                                    <div className="d-flex justify-content-start gap-3 mt-2 pt-2" style={darkThemeStyles.divider}>
                                        <small style={darkThemeStyles.mutedText} className="d-flex align-items-center gap-1">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="11" viewBox="0 0 13 11" fill="none">
                                                <path d="M9.19225 0.228027C8.62945 0.236781 8.07893 0.393887 7.59628 0.683479C7.11362 0.973071 6.71593 1.3849 6.44336 1.87736C6.17079 1.3849 5.7731 0.973071 5.29044 0.683479C4.80779 0.393887 4.25727 0.236781 3.69447 0.228027C2.7973 0.267007 1.95202 0.65948 1.34331 1.3197C0.734592 1.97993 0.411917 2.85423 0.445779 3.75161C0.445779 6.02419 2.83781 8.50619 4.844 10.189C5.29193 10.5654 5.85827 10.7718 6.44336 10.7718C7.02845 10.7718 7.59479 10.5654 8.04271 10.189C10.0489 8.50619 12.4409 6.02419 12.4409 3.75161C12.4748 2.85423 12.1521 1.97993 11.5434 1.3197C10.9347 0.65948 10.0894 0.267007 9.19225 0.228027Z" fill="#E05F5F" />
                                            </svg>
                                            12
                                        </small>
                                        <small style={darkThemeStyles.mutedText} className="d-flex align-items-center gap-1">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
                                                <path d="M6.44761 12.4165C6.2096 12.4165 5.97009 12.3325 5.77908 12.163L3.905 10.5844H2.44344C1.3404 10.5844 0.44336 9.68738 0.44336 8.58433V2.58358C0.44286 1.48053 1.3399 0.583496 2.44294 0.583496H10.4433C11.5463 0.583496 12.4434 1.48053 12.4434 2.58358V8.58383C12.4434 9.68688 11.5463 10.5839 10.4433 10.5839H9.01722L7.09364 12.174C6.91263 12.335 6.68112 12.416 6.44711 12.416L6.44761 12.4165ZM2.44294 1.58354C1.89142 1.58354 1.4429 2.03206 1.4429 2.58358V8.58383C1.4429 9.13535 1.89142 9.58387 2.44294 9.58387H4.08751C4.20552 9.58387 4.31952 9.62537 4.41002 9.70138L6.43361 11.4064L8.51869 9.69838C8.6087 9.62437 8.7212 9.58387 8.83771 9.58387H10.4438C10.9953 9.58387 11.4438 9.13535 11.4438 8.58383V2.58358C11.4438 2.03206 10.9953 1.58354 10.4438 1.58354H2.44294Z" fill="#848484" />
                                            </svg>
                                            81
                                        </small>
                                        <small style={darkThemeStyles.mutedText} className="d-flex align-items-center gap-1">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
                                                <path d="M10.112 7.83181C9.73442 7.83212 9.36256 7.9243 9.02853 8.10041C8.6945 8.27652 8.40834 8.53126 8.19473 8.84265L4.94397 7.37483C5.17486 6.81729 5.17575 6.19104 4.94646 5.63283L8.19274 4.15753C8.50931 4.61538 8.9795 4.9446 9.51801 5.08548C10.0565 5.22636 10.6277 5.16957 11.1279 4.92539C11.6282 4.68122 12.0243 4.26585 12.2445 3.75462C12.4647 3.24339 12.4944 2.67017 12.3282 2.13893C12.1619 1.60769 11.8108 1.15362 11.3385 0.859089C10.8662 0.564556 10.3039 0.449068 9.75372 0.533573C9.20353 0.618077 8.70185 0.896978 8.33969 1.31968C7.97752 1.74239 7.77885 2.2809 7.77971 2.83754C7.78183 2.96929 7.79519 3.10062 7.81966 3.23009L4.36863 4.79829C4.03718 4.48776 3.62224 4.2808 3.17478 4.20285C2.72733 4.12491 2.26685 4.17936 1.84992 4.35952C1.43299 4.53969 1.07777 4.83771 0.827888 5.21699C0.57801 5.59627 0.444363 6.04028 0.443365 6.49447C0.442367 6.94866 0.574062 6.39325 0.822271 6.77362C1.07048 7.15399 1.42439 7.45358 1.84053 7.63557C2.25666 7.81757 2.7169 7.87404 3.16469 7.79806C3.61248 7.72208 4.02832 7.51695 4.36114 7.20788L7.82116 7.77009C7.79713 7.89944 7.78393 8.0306 7.78171 8.1621C7.78161 8.6232 7.91824 9.0739 8.17432 9.4573C8.43039 9.8406 8.79441 10.1395 9.22034 10.3159C9.64626 10.4924 10.115 10.5386 10.5671 10.4487C11.0193 10.3588 11.4347 10.1368 11.7607 9.8108C12.0867 9.4848 12.3087 9.0694 12.3986 8.6172C12.4885 8.1651 12.4423 7.69636 12.2658 7.27044C12.0894 6.84451 11.7905 6.48049 11.4072 6.22442C11.0238 5.96834 10.5731 5.83171 10.112 5.83181Z" fill="#848484" />
                                            </svg>
                                            12
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </CardBody>

                    </Card>
                </ModalBody>
            </Modal>

            {/* Visibility Settings Modal */}
            <Modal isOpen={showVisibilityModal} toggle={() => setShowVisibilityModal(false)} backdrop={false}>
                <ModalHeader toggle={() => setShowVisibilityModal(false)}>Post Visibility</ModalHeader>
                <ModalBody>
                    <div className="d-flex flex-column gap-3">
                        <div
                            className={`d-flex justify-content-between align-items-center p-3 rounded cursor-pointer ${passwordProtectionData.type === 'public' ? 'bg-light' : ''}`}
                            onClick={() => handlePasswordProtectionChange('public')}
                        >
                            <div className="d-flex align-items-center gap-3">
                                <div className={`rounded-circle p-2 ${passwordProtectionData.type === 'public' ? 'bg-primary text-white' : 'bg-light'}`}>
                                    <i className="ri-group-line"></i>
                                </div>
                                <div>
                                    <h6 className="mb-0">Public</h6>
                                    <small className="text-muted">Anyone can see your post</small>
                                </div>
                            </div>
                            {passwordProtectionData.type === 'public' ? (
                                <FaDotCircle color="#6709f5" size={20} />
                            ) : (
                                <FiCircle color="#888" size={20} />
                            )}
                        </div>

                        <div
                            className={`d-flex justify-content-between align-items-center p-3 rounded cursor-pointer ${passwordProtectionData.type === 'private' ? 'bg-light' : ''}`}
                            onClick={() => handlePasswordProtectionChange('private')}
                        >
                            <div className="d-flex align-items-center gap-3">
                                <div className={`rounded-circle p-2 ${passwordProtectionData.type === 'private' ? 'bg-primary text-white' : 'bg-light'}`}>
                                    <i className="ri-user-line"></i>
                                </div>
                                <div>
                                    <h6 className="mb-0">Private</h6>
                                    <small className="text-muted">Only you can see your post</small>
                                </div>
                            </div>
                            {passwordProtectionData.type === 'private' ? (
                                <FaDotCircle color="#6709f5" size={20} />
                            ) : (
                                <FiCircle color="#888" size={20} />
                            )}
                        </div>

                        <div
                            className={`d-flex justify-content-between align-items-center p-3 rounded cursor-pointer ${passwordProtectionData.type === 'password_protected' ? 'bg-light' : ''}`}
                            onClick={() => handlePasswordProtectionChange('password_protected')}
                        >
                            <div className="d-flex align-items-center gap-3">
                                <div className={`rounded-circle p-2 ${passwordProtectionData.type === 'password_protected' ? 'bg-primary text-white' : 'bg-light'}`}>
                                    <i className="ri-lock-line"></i>
                                </div>
                                <div>
                                    <h6 className="mb-0">Password Protected</h6>
                                    <small className="text-muted">Only people with password can view</small>
                                </div>
                            </div>
                            {passwordProtectionData.type === 'password_protected' ? (
                                <FaDotCircle color="#6709f5" size={20} />
                            ) : (
                                <FiCircle color="#888" size={20} />
                            )}
                        </div>

                        {passwordProtectionData.type === 'password_protected' && (
                            <FormGroup>
                                <Label>Password</Label>
                                <InputGroup>
                                    <Input
                                        type={passwordVisible ? 'text' : 'password'}
                                        placeholder="Enter password"
                                        value={passwordProtectionData.password}
                                        onChange={handlePasswordChange}
                                    />
                                    <Button
                                        color="light"
                                        onClick={togglePasswordVisibility}
                                    >
                                        <i className={`ri-eye${passwordVisible ? '-off' : ''}-line`}></i>
                                    </Button>
                                </InputGroup>
                            </FormGroup>
                        )}
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setShowVisibilityModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        onClick={handleVisibilityModalSubmit}
                        disabled={passwordProtectionData.type === 'password_protected' && !passwordProtectionData.password.trim()}
                    >
                        Apply
                    </Button>
                </ModalFooter>
            </Modal>
        </React.Fragment>
    );
};

export default connect(null, { setBreadcrumbItems })(FeedsManagement);